import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight } from 'lucide-react';
import { ReportGuideIcon } from '../../../components/reports/ReportGuide';
import { useNavigation } from '../../../hooks/use-navigation';
import { ENGAGE_FEATURED_REPORT, ENGAGE_REPORT_GROUPS } from '../../../constants/reportCatalog';

// Same visibility rule as the main Reports index: show what the sidebar offers.
const collectHrefs = (items, out = new Set()) => {
  (items || []).forEach(item => {
    if (item?.href) out.add(item.href);
    if (item?.subItems) collectHrefs(item.subItems, out);
  });
  return out;
};

export default function ReportsHub() {
  const { navItems } = useNavigation();
  const allowed = useMemo(() => collectHrefs(navItems), [navItems]);
  const groups = ENGAGE_REPORT_GROUPS
    .map(g => ({ ...g, reports: g.reports.filter(r => allowed.has(r.route)) }))
    .filter(g => g.reports.length > 0);
  const FeaturedIcon = ENGAGE_FEATURED_REPORT.icon;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
          <BarChart3 className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Reports</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Grouped by when you need them — click ⓘ on any report to see what it is for
          </p>
        </div>
      </div>

      {allowed.has(ENGAGE_FEATURED_REPORT.route) && (
        <Link to={ENGAGE_FEATURED_REPORT.route}
          className="group flex items-center gap-4 p-4 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm flex-shrink-0">
            <FeaturedIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{ENGAGE_FEATURED_REPORT.title}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ENGAGE_FEATURED_REPORT.description}</p>
          </div>
          <ReportGuideIcon route={ENGAGE_FEATURED_REPORT.route} />
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
        </Link>
      )}

      {groups.map(group => (
        <section key={group.key} className="space-y-3">
          <div className="flex items-baseline gap-3 border-b border-gray-100 dark:border-gray-700 pb-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{group.title}</h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{group.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {group.reports.map(r => (
              <Link key={r.route} to={r.route}
                className="group bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${r.color} flex items-center justify-center text-white shadow-sm flex-shrink-0`}>
                    <r.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                        {r.title}
                      </h3>
                      <div className="flex items-center gap-1">
                        <ReportGuideIcon route={r.route} />
                        <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{r.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
